/**
 * Bulk Match Modal
 * ================
 * Modal for running bulk invoice-quote matching analysis.
 *
 * Uses WebSocket for real-time progress updates during analysis.
 *
 * WebSocket Events:
 *   - match_progress: Updates progress bar as each pair is processed
 *   - match_complete: Notifies when analysis is complete
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useSocket, MatchProgressEvent, MatchCompleteEvent } from '../../../context/SocketContext';
import documentApi, { DocumentItem } from '../../../services/documentApi';
import './BulkMatchModal.scss';

interface BulkMatchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMatchStart: () => void;
    onMatchComplete: (results: any[]) => void;
}

interface ProgressState {
    current: number;
    total: number;
    percent: number;
    latestResult?: {
        invoice_id: string;
        quote_id: string;
        match_score: number;
        status: string;
    };
}

const BulkMatchModal: React.FC<BulkMatchModalProps> = ({
    isOpen,
    onClose,
    onMatchStart,
    onMatchComplete
}) => {
    const { token } = useAuth();
    const { subscribe, unsubscribe, on, off, isConnected } = useSocket();

    const [invoices, setInvoices] = useState<DocumentItem[]>([]);
    const [rfqs, setRfqs] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Selections
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
    const [selectedRfqIds, setSelectedRfqIds] = useState<Set<string>>(new Set());

    const [analyzing, setAnalyzing] = useState(false);
    const [progress, setProgress] = useState<ProgressState | null>(null);

    // Toggle for unmatched-only filtering
    const [matchOnlyUnmatched, setMatchOnlyUnmatched] = useState(true);

    // Track current job for WebSocket events
    const currentJobRef = useRef<string | null>(null);

    // Handle progress events from WebSocket
    const handleMatchProgress = useCallback((event: MatchProgressEvent) => {
        if (event.job_id !== currentJobRef.current) return;

        setProgress({
            current: event.current,
            total: event.total,
            percent: event.percent,
            latestResult: event.result
        });
    }, []);

    // Handle completion events from WebSocket
    const handleMatchComplete = useCallback((event: MatchCompleteEvent) => {
        if (event.job_id !== currentJobRef.current) return;

        // Cleanup
        if (currentJobRef.current) {
            unsubscribe('job', currentJobRef.current);
        }
        currentJobRef.current = null;

        // Note: Actual results come from API response, not WebSocket
        // WebSocket just provides progress updates
    }, [unsubscribe]);

    // Register WebSocket listeners
    useEffect(() => {
        if (!isConnected) return;

        on<MatchProgressEvent>('match_progress', handleMatchProgress);
        on<MatchCompleteEvent>('match_complete', handleMatchComplete);

        return () => {
            off('match_progress', handleMatchProgress);
            off('match_complete', handleMatchComplete);
        };
    }, [isConnected, on, off, handleMatchProgress, handleMatchComplete]);

    useEffect(() => {
        if (isOpen && token) {
            loadData();
        }
    }, [isOpen, token, matchOnlyUnmatched]);

    // Cleanup on unmount or close
    useEffect(() => {
        return () => {
            if (currentJobRef.current) {
                unsubscribe('job', currentJobRef.current);
            }
        };
    }, [unsubscribe]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const matchStatus = matchOnlyUnmatched ? 'unmatched' : undefined;

            const invRes = await documentApi.getDocuments(1, 100, 'invoice', matchStatus);
            if (invRes.success && invRes.data) {
                setInvoices(invRes.data.documents);
            }

            const quoteRes = await documentApi.getDocuments(1, 100, 'quote');
            if (quoteRes.success && quoteRes.data) {
                let quotes = quoteRes.data.documents;

                if (matchOnlyUnmatched) {
                    const allInvRes = await documentApi.getDocuments(1, 100, 'invoice');
                    if (allInvRes.success && allInvRes.data) {
                        const linkedQuoteIds = new Set(
                            allInvRes.data.documents
                                .map(inv => inv.comparison?.linked_quote_id)
                                .filter(id => id != null)
                        );
                        quotes = quotes.filter(q => !linkedQuoteIds.has(q.id));
                    }
                }

                setRfqs(quotes);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRunAnalysis = async () => {
        if (selectedInvoiceIds.size === 0 || selectedRfqIds.size === 0) return;

        setAnalyzing(true);
        setProgress({ current: 0, total: selectedInvoiceIds.size, percent: 0 });
        onMatchStart();

        try {
            const invoiceList = Array.from(selectedInvoiceIds);
            const quoteList = Array.from(selectedRfqIds);

            const response = await documentApi.analyzeMatching(invoiceList, quoteList);

            // Subscribe to job updates for progress (job_id comes from API response)
            if (response.job_id) {
                currentJobRef.current = response.job_id;
                subscribe('job', response.job_id);
            }

            if (response.success && response.data) {
                // Set progress to 100% when complete
                setProgress(prev => prev ? { ...prev, current: prev.total, percent: 100 } : null);
                onMatchComplete(response.data);
                onClose();
            } else {
                alert('Analysis failed: ' + response.message);
            }
        } catch (error) {
            console.error(error);
            alert('Analysis failed');
        } finally {
            setAnalyzing(false);
            setProgress(null);

            // Cleanup job subscription
            if (currentJobRef.current) {
                unsubscribe('job', currentJobRef.current);
                currentJobRef.current = null;
            }
        }
    };

    const toggleInvoice = (id: string) => {
        const next = new Set(selectedInvoiceIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedInvoiceIds(next);
    };

    const toggleRfq = (id: string) => {
        const next = new Set(selectedRfqIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedRfqIds(next);
    };

    const selectAllInvoices = () => {
        if (selectedInvoiceIds.size === invoices.length) setSelectedInvoiceIds(new Set());
        else setSelectedInvoiceIds(new Set(invoices.map(i => i.id)));
    };

    const selectAllRfqs = () => {
        if (selectedRfqIds.size === rfqs.length) setSelectedRfqIds(new Set());
        else setSelectedRfqIds(new Set(rfqs.map(r => r.id)));
    };

    if (!isOpen) return null;

    return (
        <div className="bulk-match-modal-overlay">
            <div className="bulk-match-modal">
                <div className="bulk-match-modal__header">
                    <h2>Run Bulk Analysis</h2>
                    <button onClick={onClose} className="bulk-match-modal__close" disabled={analyzing}>&times;</button>
                </div>

                <div className="bulk-match-modal__body">
                    <p className="bulk-match-modal__description">
                        Select invoices and quotes to run the matching algorithm against each other.
                    </p>

                    {/* Progress Bar - Show during analysis */}
                    {analyzing && progress && (
                        <div className="bulk-match-modal__progress">
                            <div className="bulk-match-modal__progress-bar">
                                <div
                                    className="bulk-match-modal__progress-fill"
                                    style={{ width: `${progress.percent}%` }}
                                />
                            </div>
                            <div className="bulk-match-modal__progress-text">
                                Processing {progress.current} of {progress.total} pairs ({progress.percent}%)
                                {progress.latestResult && (
                                    <span className="bulk-match-modal__progress-status">
                                        {' '}- Last: {progress.latestResult.status}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="bulk-match-modal__toggle">
                        <input
                            type="checkbox"
                            id="match-unmatched-toggle"
                            checked={matchOnlyUnmatched}
                            onChange={(e) => {
                                setMatchOnlyUnmatched(e.target.checked);
                                setSelectedInvoiceIds(new Set());
                                setSelectedRfqIds(new Set());
                            }}
                            disabled={analyzing}
                        />
                        <label htmlFor="match-unmatched-toggle">
                            Only show unmatched documents (recommended)
                        </label>
                        <span className="bulk-match-modal__toggle-hint">
                            {matchOnlyUnmatched ? 'Smart mode' : 'Showing all'}
                        </span>
                    </div>

                    {isLoading ? (
                        <div className="bulk-match-modal__loading">Loading documents...</div>
                    ) : (
                        <div className="bulk-match-modal__grid">
                            {/* Invoices Column */}
                            <div className="bulk-match-modal__column">
                                <div className="bulk-match-modal__column-header">
                                    <h4>Invoices ({selectedInvoiceIds.size}/{invoices.length})</h4>
                                    <button onClick={selectAllInvoices} disabled={analyzing}>
                                        {selectedInvoiceIds.size === invoices.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="bulk-match-modal__list">
                                    {invoices.map(inv => (
                                        <div
                                            key={inv.id}
                                            className={`bulk-match-modal__item ${selectedInvoiceIds.has(inv.id) ? 'selected' : ''} ${analyzing ? 'disabled' : ''}`}
                                            onClick={() => !analyzing && toggleInvoice(inv.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedInvoiceIds.has(inv.id)}
                                                readOnly
                                                disabled={analyzing}
                                            />
                                            <div className="bulk-match-modal__item-info">
                                                <span className="bulk-match-modal__item-name">{inv.filename}</span>
                                                <span className="bulk-match-modal__item-date">
                                                    {new Date(inv.created_at).toLocaleDateString('tr-TR')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    {invoices.length === 0 && (
                                        <div className="bulk-match-modal__empty">No invoices found</div>
                                    )}
                                </div>
                            </div>

                            {/* Quotes Column */}
                            <div className="bulk-match-modal__column">
                                <div className="bulk-match-modal__column-header">
                                    <h4>Quotes ({selectedRfqIds.size}/{rfqs.length})</h4>
                                    <button onClick={selectAllRfqs} disabled={analyzing}>
                                        {selectedRfqIds.size === rfqs.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="bulk-match-modal__list">
                                    {rfqs.map(rfq => (
                                        <div
                                            key={rfq.id}
                                            className={`bulk-match-modal__item ${selectedRfqIds.has(rfq.id) ? 'selected' : ''} ${analyzing ? 'disabled' : ''}`}
                                            onClick={() => !analyzing && toggleRfq(rfq.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedRfqIds.has(rfq.id)}
                                                readOnly
                                                disabled={analyzing}
                                            />
                                            <div className="bulk-match-modal__item-info">
                                                <span className="bulk-match-modal__item-name">
                                                    {rfq.filename || rfq.product_name || 'Unnamed Document'}
                                                </span>
                                                <span className="bulk-match-modal__item-date">
                                                    {new Date(rfq.created_at).toLocaleDateString('tr-TR')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    {rfqs.length === 0 && (
                                        <div className="bulk-match-modal__empty">No quotes found</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bulk-match-modal__footer">
                    <button onClick={onClose} disabled={analyzing} className="bulk-match-modal__btn-cancel">
                        Cancel
                    </button>
                    <button
                        onClick={handleRunAnalysis}
                        className="bulk-match-modal__btn-primary"
                        disabled={analyzing || selectedInvoiceIds.size === 0 || selectedRfqIds.size === 0}
                    >
                        {analyzing
                            ? `Analyzing... ${progress?.percent || 0}%`
                            : `Run Analysis (${selectedInvoiceIds.size} vs ${selectedRfqIds.size})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkMatchModal;
